import { AnimatePresence, motion } from 'motion/react'
import { Settings2, X } from 'lucide-react'
import { t } from '../i18n'
import { mappingOf } from '../lib/gerber'
import type { GerberFile } from '../types'

interface Props {
  open: boolean
  files: GerberFile[]
  onClose: () => void
  onToggle: (id: string, enabled: boolean) => void
  onAll: (enabled: boolean) => void
  onManage: () => void
}

const typeColor: Record<string, string> = {
  copper: 'var(--copper)',
  soldermask: 'var(--mask)',
  silkscreen: 'var(--silk)',
  solderpaste: 'var(--paste)',
  drill: 'var(--drill)',
  outline: 'var(--outline)',
  drawing: 'var(--muted)',
}

export function LayerPanel({ open, files, onClose, onToggle, onAll, onManage }: Props) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="layer-panel"
          role="dialog"
          aria-label={t('layers.title')}
          initial={{ opacity: 0, y: -8, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.99 }}
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
        >
          <header className="layer-head">
            <div>
              <strong>{t('layers.title')}</strong>
              <span>{t('layers.count', { n: files.length })}</span>
            </div>
            <div className="layer-head-actions">
              <button type="button" onClick={() => onAll(true)}>{t('layers.all')}</button>
              <button type="button" onClick={() => onAll(false)}>{t('layers.none')}</button>
              <button className="icon-btn tiny" type="button" onClick={onClose} aria-label={t('common.close')}>
                <X size={14} />
              </button>
            </div>
          </header>

          <div className="layer-list">
            {files.length === 0 ? <p className="layer-empty">{t('layers.empty')}</p> : null}
            {files.map((file) => {
              const mapping = mappingOf(file)
              const color = typeColor[mapping.type ?? 'drawing'] ?? 'var(--muted)'
              return (
                <div className={`layer-row ${file.enabled ? '' : 'is-off'}`} key={file.id}>
                  <span className="layer-dot" style={{ background: color }} />
                  <span className="layer-name" title={file.name}>{file.name}</span>
                  <span className="layer-tags">
                    <em>{mapping.type ? t(`layer.type.${mapping.type}`) : t('layers.unmapped')}</em>
                    {mapping.side ? <em className="side">{t(`layer.side.${mapping.side}`)}</em> : null}
                  </span>
                  <button
                    className={`switch ${file.enabled ? 'is-on' : ''}`}
                    type="button"
                    role="switch"
                    aria-checked={file.enabled}
                    aria-label={t('layers.toggle', { name: file.name })}
                    onClick={() => onToggle(file.id, !file.enabled)}
                  >
                    <span />
                  </button>
                </div>
              )
            })}
          </div>

          <footer className="layer-foot">
            <button className="link-btn" type="button" onClick={onManage}>
              <Settings2 size={13} />
              {t('layers.manage')}
            </button>
          </footer>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
